import { useMemo, useSyncExternalStore } from 'react';

export type Route =
  | { name: 'daily' }
  | { name: 'journey' }
  | { name: 'challenge'; code: string; hash: string }
  | { name: 'new-challenge' }
  | { name: 'my-challenges' }
  | { name: 'not-found'; path: string };

/** Dispatched by `navigate` so `useRoute` re-reads without a popstate. */
export const NAVIGATE_EVENT = 'lexicon:navigate';

/** Challenge codes are 5-char base58 (spec §4.3); accept a tolerant range. */
const CODE_RE = /^[A-Za-z0-9]{3,12}$/;

/**
 * Pure path → route. Trailing slashes and empty paths normalise to `/`.
 *
 * `hash` is carried on the challenge route only, and it is not decoration:
 * every offline challenge link shares the single route param `offline`
 * (spec §4.2), so the fragment is the *only* thing that tells two of them
 * apart. Without it here, following a second offline link would leave the
 * first word playing (F1).
 */
export function parseRoute(pathname: string, hash = ''): Route {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return { name: 'daily' };
  if (path === '/journey') return { name: 'journey' };
  if (path === '/challenge/new') return { name: 'new-challenge' };
  if (path === '/challenges') return { name: 'my-challenges' };
  const challenge = /^\/c\/([^/]+)$/.exec(path);
  if (challenge && CODE_RE.test(challenge[1]!)) {
    return { name: 'challenge', code: challenge[1]!, hash };
  }
  return { name: 'not-found', path };
}

/** Push a path (fragment included) and notify subscribers. */
export function navigate(path: string): void {
  if (path !== `${window.location.pathname}${window.location.hash}`) {
    window.history.pushState(null, '', path);
  }
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange);
  // Following a link to a different fragment of the same path fires
  // hashchange and nothing else — no popstate, no NAVIGATE_EVENT (F1).
  window.addEventListener('hashchange', onChange);
  window.addEventListener(NAVIGATE_EVENT, onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener('hashchange', onChange);
    window.removeEventListener(NAVIGATE_EVENT, onChange);
  };
}

/**
 * One string, because `useSyncExternalStore` compares snapshots by
 * identity: returning a fresh object here would re-render forever.
 */
function getSnapshot(): string {
  return `${window.location.pathname}${window.location.hash}`;
}

/** Current route; re-renders on `navigate`, back/forward and fragment changes. */
export function useRoute(): Route {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return useMemo(() => {
    const hashAt = snapshot.indexOf('#');
    return hashAt === -1
      ? parseRoute(snapshot)
      : parseRoute(snapshot.slice(0, hashAt), snapshot.slice(hashAt));
  }, [snapshot]);
}
